import { Button, Controller, Typography, useForm } from '@otp/ui';
import { useTranslation } from 'react-i18next';
import { MostRequestHistoryListDocument } from 'src/graphql/generated/graphql';
import { MostJobConfig, useRunCommand } from 'src/hooks/query/use-run-command';
import { ActionField } from '../scheduled-jobs/fields/action';
import { DevicesField } from '../scheduled-jobs/fields/devices';
import { DuplicateConfigAlert } from '../scheduled-jobs/form/duplicate-config-alert';
import { queryStringToObject } from 'src/utils/query-string';
import { useSearchParams } from 'react-router-dom';
import { StoresServerSideField } from 'src/components/common/form/fields/stores-serverside';

type Form = MostJobConfig;

export const ActionBar = () => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'most.list.run.form',
  });

  const [searchParams] = useSearchParams();

  const defaultValues = queryStringToObject(searchParams.toString())?.filter;

  const { submitJobConfig, loading } = useRunCommand();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm<Form>({ defaultValues });

  const storeIds = watch('storeIds');
  const command = watch('command');
  const devices = watch('devices') || [];

  const onSubmit = async (form: Form) => {
    submitJobConfig(form, {
      refetchQueries: [MostRequestHistoryListDocument],
      onFinish: () => reset({}),
    });
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-between items-end gap-2 mb-2">
          <Controller
            name="storeIds"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="flex-1">
                <Typography
                  className="mb-2"
                  variant="subtitle1"
                  color={!!errors.storeIds ? 'red' : ''}
                >
                  {t('stores')}
                  <span className="text-red-500"> *</span>
                </Typography>
                <StoresServerSideField useCheckboxOption field={field} multiple />
              </div>
            )}
          />
          <Controller
            name="command"
            control={control}
            rules={{
              required: true,
              validate: (command) => {
                if (typeof command === 'undefined') {
                  return false;
                }

                if (typeof command.commandId === 'undefined') {
                  return false;
                }

                if (typeof command.type === 'undefined') {
                  return false;
                }
              },
            }}
            render={({ field }) => (
              <div className="flex-1">
                <Typography
                  className="mb-2"
                  variant="subtitle1"
                  color={!!errors.command ? 'red' : ''}
                >
                  {t('action')}
                  <span className="text-red-500"> *</span>
                </Typography>
                <ActionField field={field} />
              </div>
            )}
          />
          <Controller
            name="devices"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className="flex-1">
                <Typography
                  className="mb-2"
                  variant="subtitle1"
                  color={!!errors.devices ? 'red' : ''}
                >
                  {t('devices')}
                  <span className="text-red-500"> *</span>
                </Typography>
                <DevicesField
                  field={field}
                  filter={{ storeIds, action: command }}
                />
              </div>
            )}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="medium"
            disabled={loading}
          >
            {t('submit')}
          </Button>
        </div>
      </form>

      <DuplicateConfigAlert commandId={command?.commandId} devices={devices} />
    </div>
  );
};
